import styled from '@emotion/styled';

/**
 * IntroPage — Page 2 of the Kuutio print report.
 *
 * Layout from IDML Spread_u8b1.xml with exact positions.
 * Two-column: left text, right cube + process diagram + info box.
 *
 * Colors (from IDML Graphic.xml CMYK → approx RGB):
 *   Voikukka (yellow): CMYK 2,9,100,0 → #FBE300
 *   Tellus (blue): CMYK 49,0,5,0 → #7CCDE4
 *   Mansikkasmoothie (pink): CMYK 0,22,3,0 → #FCCDD3
 *   Tyrni copy (olive): CMYK 44,6,43,0 → #8FBD8B
 */
export default function IntroPage({
  date = '20.3.2026',
  instanceNumber = '3',
  topic = 'Kansainvälisten opiskelijoiden työllistyminen Suomeen',
}) {
  return (
    <Page className="page intro-page">
      {/* Left column: title + body text + contact */}
      <TitleFrame>
        <Title>TYÖSKENTELYN YHTEENVETO</Title>
        <DateText>{date}</DateText>
      </TitleFrame>
      <YellowRule />

      <BodyFrame>
        <Body>
          Tämä on Kuutio #3 yhteenveto: Kansainvälisten opiskelijoiden
          työllistyminen Suomeen. Jotta kansainvälisten korkeakoulu­opiskelijoiden
          työllistyminen onnistuu, tarvitsemme yhteiskunnan eri toimijoiden yhdessä
          kehittämiä, kaikkia osapuolia hyödyttäviä pitkäjänteisiä ratkaisuja.
        </Body>
        <Body>
          Kuutio on Sitran kehittämä menetelmä ja uudenlainen tapa tart­tua
          yhteiskunnallisiin haasteisiin, tarkastella niitä datapohjaisesti
          ja tulevaisuusorientoituneesti sekä löytää toimivia ratkaisuja.
        </Body>
        <Body>
          Kuutioon valitaan yhteiskunnallisesti tärkeä aihe. Noin 20–30
          asiantuntijaa ja päätöksentekijää kutsutaan kahdeksi päiväksi
          työskentelemään aiheen parissa.
        </Body>
        <Body>
          Kuutio auttaa muodostamaan yhteisen tilannekuvan ja kehittä­mään
          ratkaisuja datapohjaisesti pitkä aikaväli ja erilaiset näkökul­mat
          huomioiden.
        </Body>
        <Body>
          Historian kolmannessa Kuutiossa maaliskuussa 2026 yhteensä 32
          asiantuntijaa ja päättäjää työskenteli kaksi päivää tekoälyavustei­sesti
          pohtien kysymystä kansainvälisten opiskelijoiden työllistymi­sestä
          Suomessa.
        </Body>
        <Body>
          Yhteenvetoon on dokumentoitu Kuution eri vaiheet ja
          osallistujien tuottamat materiaalit. Yhteenveto sisältää myös
          osallistujien työskentelyn pohjalta kokoamia ratkaisuehdotuksia.
          Osallistujalista on liitteenä.
        </Body>
      </BodyFrame>

      <ContactFrame>
        <ContactBorder />
        <ContactContent>
          <ContactTitle>Lisätietoja</ContactTitle>
          <ContactText>Saara Saarinen</ContactText>
          <ContactText>Johtava asiantuntija, Sitra</ContactText>
          <ContactText>saara.saarinen@sitra.fi</ContactText>
        </ContactContent>
      </ContactFrame>

      {/* Right column: cube + process diagram + info box */}
      <CubeFrame>
        <img
          src="/assets/report/kuutio-logo-white.png"
          alt="Kuutio"
          style={{ width: '100%', height: 'auto' }}
        />
      </CubeFrame>

      <ProcessLabel>KUUTION PROSESSI</ProcessLabel>
      <ProcessDivider />

      {/* 4 circles: 3 small (33mm) + 1 large center (44.6mm), overlapping */}
      <BubbleContainer>
        <BubbleSmall $color="#FBE300" $left="0">
          <BubbleText>AIHEEN{'\n'}VALINTA</BubbleText>
        </BubbleSmall>
        <BubbleSmall $color="#8FBD8B" $left="28mm">
          <BubbleText>TIEDON-{'\n'}TUOTANTO</BubbleText>
        </BubbleSmall>
        <BubbleLarge $color="#7CCDE4">
          <BubbleText>KUUTIO{'\n'}2 PÄIVÄÄ</BubbleText>
        </BubbleLarge>
        <BubbleSmall $color="#FCCDD3" $left="93mm">
          <BubbleText>KUUTIO-{'\n'}RAATI</BubbleText>
        </BubbleSmall>
      </BubbleContainer>

      {/* Thin connector lines — from IDML: uad2 and u40e4 */}
      <ConnectorLine $left="665.6pt" $top="440.0pt" $height="20.2pt" />
      <ConnectorLine $left="756.7pt" $top="419.6pt" $height="40.6pt" />

      {/* Blue info box with left border */}
      <InfoBox>
        <InfoBorder />
        <InfoContent>
          <InfoText>
            Tässä työskentelyn yhteenvedossa kuvataan kahden päivän{' '}
            <strong>Kuutio-työskentely 18.–19.3.2026</strong> sekä Kuutio-raati{' '}
            <strong>25.3.2026.</strong>
          </InfoText>
          <InfoText>
            Sitran asiantuntijat ovat koonneet yhteenvedon Kuutio-työskentelyn
            pohjalta tekoälyä hyödyntäen.
          </InfoText>
        </InfoContent>
      </InfoBox>

      <PageFooter>
        <FooterLeft><strong>Kuutio #{instanceNumber}:</strong> {topic}</FooterLeft>
        <FooterRight>2</FooterRight>
      </PageFooter>
    </Page>
  );
}

/* Page container — A4 landscape */
const Page = styled.div`
  width: 297mm;
  min-height: 210mm;
  background: #fff;
  color: #000;
  position: relative;
  font-family: 'Rational', sans-serif;
  box-sizing: border-box;
  overflow: hidden;

  @media print {
    break-after: page;
  }
`;

/* --- Left column elements --- */

/* Title: Otsikot style — Bold Italic, 22pt, ALL CAPS */
const TitleFrame = styled.div`
  position: absolute;
  left: 42.5pt;
  top: 29pt;
  width: 370pt;
  display: flex;
  justify-content: space-between;
  align-items: baseline;
`;

const Title = styled.h1`
  font-size: 22pt;
  font-weight: 700;
  font-style: italic;
  text-transform: uppercase;
  letter-spacing: 0.3px;
  margin: 0;
`;

const DateText = styled.span`
  font-size: 12pt;
  font-style: italic;
  font-weight: 400;
  /* Date positioned at far right of the full page width */
  position: absolute;
  right: -400pt;
`;

/* Yellow underline rule below title */
const YellowRule = styled.div`
  position: absolute;
  left: 42.5pt;
  top: 58pt;
  /* Spans full page width minus margins */
  width: calc(297mm - 42.5pt - 28.3pt);
  height: 3pt;
  background: #FBE300;
`;

/* Body text: Leipis style — Book, 11pt, 13pt leading */
const BodyFrame = styled.div`
  position: absolute;
  left: 42.8pt;
  top: 77pt;
  width: 354pt;
`;

const Body = styled.p`
  font-size: 11pt;
  font-weight: 400;
  line-height: 13pt;
  margin: 0 0 10pt 0;
`;

/* Contact info at bottom-left with yellow left border */
const ContactFrame = styled.div`
  position: absolute;
  left: 42.5pt;
  top: 480pt;
  display: flex;
`;

const ContactBorder = styled.div`
  width: 3pt;
  background: #FBE300;
  margin-right: 10pt;
  align-self: stretch;
`;

const ContactContent = styled.div``;

const ContactTitle = styled.div`
  font-size: 11pt;
  font-weight: 700;
  margin-bottom: 1pt;
`;

const ContactText = styled.div`
  font-size: 11pt;
  font-weight: 400;
  line-height: 13pt;
`;

/* --- Right column elements --- */

/* Cube image: 68.4mm wide, positioned at 63.5% left, 13.3% top */
const CubeFrame = styled.div`
  position: absolute;
  left: 534pt;
  top: 79pt;
  width: 194pt;
`;

/* "KUUTION PROSESSI" label — italic, small caps feel */
const ProcessLabel = styled.div`
  position: absolute;
  left: 458pt;
  top: 288pt;
  font-size: 10pt;
  font-style: italic;
  font-weight: 400;
  letter-spacing: 1.5px;
`;

/* Thin horizontal divider below label */
const ProcessDivider = styled.div`
  position: absolute;
  left: 457pt;
  top: 306pt;
  width: 354pt;
  height: 0.225pt;
  background: rgba(0, 0, 0, 0.52);
`;

/* Bubble container — positioned absolutely */
const BubbleContainer = styled.div`
  position: absolute;
  left: 449pt;
  top: 313pt;
  width: 362pt;
  height: 127pt;
`;

/* Small bubble: 93pt (33mm) diameter */
const BubbleSmall = styled.div`
  position: absolute;
  left: ${props => props.$left};
  top: 13pt;
  width: 93pt;
  height: 93pt;
  border-radius: 50%;
  background: ${props => props.$color};
  display: flex;
  align-items: center;
  justify-content: center;
`;

/* Large bubble (Tellus/blue): 127pt (44.6mm) diameter */
const BubbleLarge = styled.div`
  position: absolute;
  left: 55mm;
  top: 0;
  width: 127pt;
  height: 127pt;
  border-radius: 50%;
  background: #7CCDE4;
  display: flex;
  align-items: center;
  justify-content: center;
`;

/* Bubble text labels — bold italic, ~9pt */
const BubbleText = styled.span`
  font-size: 9pt;
  font-weight: 700;
  font-style: italic;
  text-align: center;
  line-height: 1.2;
  white-space: pre-line;
  color: #000;
`;

/* Thin connector lines — IDML: StrokeWeight 0.225pt, Tint 52% */
const ConnectorLine = styled.div`
  position: absolute;
  left: ${props => props.$left};
  top: ${props => props.$top};
  width: 0.225pt;
  height: ${props => props.$height};
  background: rgba(0, 0, 0, 0.52);
`;

/* Blue info box: Tellus at 33% tint + left border */
const InfoBox = styled.div`
  position: absolute;
  left: 457pt;
  top: 460pt;
  width: 354pt;
  height: 77pt;
  display: flex;
`;

const InfoBorder = styled.div`
  width: 2pt;
  background: #7CCDE4;
  flex-shrink: 0;
`;

const InfoContent = styled.div`
  background: rgba(124, 205, 228, 0.33);
  padding: 12pt 14pt;
  flex: 1;
`;

const InfoText = styled.p`
  font-size: 10pt;
  font-weight: 400;
  line-height: 13pt;
  margin: 0 0 6pt 0;

  &:last-child {
    margin-bottom: 0;
  }
`;

/* --- Footer --- */
const PageFooter = styled.div`
  position: absolute;
  bottom: 14pt;
  left: 42.5pt;
  right: 28.3pt;
  display: flex;
  justify-content: space-between;
  align-items: center;
  border-top: 0.5pt solid rgba(0, 0, 0, 0.3);
  padding-top: 6pt;
`;

const FooterLeft = styled.span`
  font-size: 8pt;
  font-style: italic;
  font-weight: 400;
`;

const FooterRight = styled.span`
  font-size: 10pt;
  font-weight: 400;
`;
